import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, computed, inject, OnDestroy, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';

import { LayoutService } from '../../layout/service/layout.service';
import { LandingBenefits } from './components/landing-benefits.component';
import { LandingChallenge } from './components/landing-challenge.component';
import { LandingContact } from './components/landing-contact.component';
import { LandingFit } from './components/landing-fit.component';
import { LandingFooter } from './components/landing-footer.component';
import { LandingHeader } from './components/landing-header.component';
import { LandingHero } from './components/landing-hero.component';
import { LandingHow } from './components/landing-how.component';
import { LandingInfo } from './components/landing-info.component';
import { LandingIntro } from './components/landing-intro.component';
import { LandingSecurity } from './components/landing-security.component';
import { LandingStarfield } from './components/landing-starfield.component';
import { LandingWork } from './components/landing-work.component';
import { LandingLanguage, LandingText, ShootingStar, SpaceParticle } from './landing.types';

import { LandinglService } from './landing.service';

const LANDING_TEXT: Record<LandingLanguage, LandingText> = {
    en: {
        skipToContentLabel: 'Skip to content',
        introBrand: 'InvoNow',
        introPresentsWord: 'A Quiet Revolution in Accounting',
        introScrollHint: 'Scroll to enter',
        appName: 'MoveNow',
        slogan: 'Elegant file processing for teams that value time.',
        navHome: 'Home',
        navWork: 'Product',
        navFit: 'Business fit',
        navBenefits: 'Business Benefits',
        navSecurity: 'Security & reliability',
        navInfo: 'Ask for a Demo',
        login: 'Log in',
        languageLabel: 'Language',
        colorLabel: 'Main color',
        themeLabel: 'Toggle theme',
        heroBadge: 'Fast. Efficient. Reliable.',
        heroTitle: 'From invoice for accounting journal order without manual work.',
        heroSubtitle: 'MoveNow reads your supplier invoices, extracts and validates the data using AI, and delivers accounting-ready records directly into your existing system—no reimplementation, no weeks of setup, no complex and complicated data conversion, and no downtime for employee training.',
        heroPrimary: 'Order a demo NOW',
        heroSecondary: 'Explore the workflow',
        heroStats: [
            { value: '24/7', description: 'Always ready for file intake and processing.' },
            { value: '3x', description: 'Less friction with streamlined workflows.' },
            { value: '∞', description: 'Scales cleanly from small teams to enterprise use.' }
        ],
        heroPreview: {
            liveStatusLabel: 'Live status',
            filePipelineLabel: 'File pipeline',
            processingBadge: 'Processing',
            intakeLabel: 'Intake',
            doneLabel: 'Done',
            processingStageLabel: 'Processing',
            fastLabel: 'Fast',
            resultsLabel: 'Results',
            readyLabel: 'Ready'
        },
        challengeTitle: 'The challenge',
        challengeSubtitle: 'The Bottleneck',
        challengeCards: [
            { title: 'Manual data entry', description: 'Every invoice field — supplier, dates, amounts, and detail lines — is typed into the accounting system by hand.' },
            { title: 'A time-consuming process', description: 'Invoices pile up in a queue, waiting for someone to have time to handle them one by one.' },
            { title: 'Human errors', description: 'Amounts, dates, and supplier details entered by mistake find their way into the books.' },
            { title: 'Inconsistent formats', description: 'Each supplier sends invoices in a different format, which makes standardization and faster processing harder.' },
            { title: 'Disconnected systems', description: 'Invoice data does not flow automatically into the accounting system — someone has to bridge the gap manually.' },
            { title: 'Repetitive accounting work', description: 'Skilled finance staff spend hours on repetitive, low-value retyping instead of higher-value work.' }
        ],
        workTitle: 'What we do',
        workSubtitle: 'We take files in, process them, and return useful results fast.',
        workCards: [
            { title: 'Intake', description: 'Collect files from your team or customers with a simple, guided flow.' },
            { title: 'Process', description: 'Transform data quickly and consistently with fewer manual steps.' },
            { title: 'Deliver', description: 'Send back organized results your team can use immediately.' }
        ],
        pipelineMock: {
            heading: 'MoveNow — Invoice Processing Pipeline',
            liveLabel: 'Live view',
            invoiceLabel: 'Invoice',
            invoiceFileName: 'Invoice_0138.pdf',
            scanLabel: 'Scan',
            emailLabel: 'Email',
            whatsappLabel: 'WhatsApp',
            aiAnalysisLabel: 'AI Analysis',
            extractingLabel: 'Extracting & validating',
            fieldsLabel: 'Vendor · Dates · Amounts · VAT',
            accountingSystemLabel: 'Accounting System',
            supplierLabel: 'Supplier',
            supplierValue: 'InvoNow Ltd',
            totalDueLabel: 'Total due',
            receivedLabel: 'Received',
            breadcrumbExtraction: 'Invoice & data extraction',
            breadcrumbAnalysis: 'Analysis',
            breadcrumbAccounting: 'Your accounting system'
        },
        howTitle: 'How it works',
        howBridgeLine: 'A quiet pipeline from invoice to journal',
        howSubtitle: 'It works alongside the way your team already works. It does not replace the accounting system — it feeds it.',
        howSteps: [
            { title: 'Intake & upload', description: 'Invoices arrive by scan or digital upload, in whatever format the suppliers send them — no special preparation needed.' },
            { title: 'AI analysis and data extraction', description: 'MoveNow reads each invoice and automatically extracts supplier details, dates, amounts, VAT, and line items.' },
            { title: 'Validation & structured data', description: 'The extracted data is checked and turned into accounting-ready records. Anything that needs a human look is flagged for a quick review — never buried in the pile.' },
            { title: 'Export & integration', description: 'Verified data flows directly into your accounting system, ready to post — without repeat typing.' }
        ],
        fitTitle: 'Built for small and big business',
        fitSubtitle: 'Whether you run a compact team or a large operation, the experience stays clean and scalable.',
        fitDescription: 'From lightweight daily use to heavy operational workloads, the same elegant flow keeps your team moving.',
        fitCards: [
            { title: 'Small teams', description: 'Move faster with simple workflows, clear status, and little overhead.' },
            { title: 'Growing companies', description: 'Keep the same smooth flow as volume increases and responsibilities expand.' },
            { title: 'Large organizations', description: 'Maintain consistency across departments with a process designed for scale.' }
        ],
        benefitsTitle: 'Business benefits',
        benefitsSubtitle: 'Automatic file processing changes how your team spends its time — starting today.',
        benefitsCards: [
            { title: 'Fewer errors', description: 'Structured, validated data reduces mistakes that reach your records.' },
            { title: 'Faster processing', description: 'Files move from inbox to result in a fraction of the time manual entry required.' },
            { title: 'Less manual work', description: 'Your team stops retyping data by hand and gets that time back for other work.' },
            { title: 'Grows with you', description: 'Handles a growing volume of files without a proportional growth in headcount.' },
            { title: 'Effortless integration', description: 'Works alongside your existing systems and processes — no heavy rollout project.' },
            { title: 'Low barrier to entry', description: 'Simple enough for any team member to use with confidence from day one.' }
        ],
        statsHeadline: 'Built to fit in, not disrupt',
        statsSubtitle: 'MoveNow is built to slot into your existing process easily — not another heavy system to roll out.',
        stats: [
            { value: '0', label: 'Infrastructure', description: 'No servers or installations on your machines.' },
            { value: '0', label: 'Data setup', description: 'No complex environment or data migration before your team can start.' },
            { value: '0', label: 'Extra headcount', description: 'No dedicated specialists needed — your existing team keeps working as usual.' }
        ],
        securityTitle: 'Security & reliability',
        securitySubtitle: 'MoveNow is built around how teams really work — with control points, not blind automation.',
        securityItems: [
            { title: 'Consistent, structured output', description: 'Every file goes through automatic validation before it reaches your system, keeping your existing data consistent.' },
            { title: 'Without disrupting your system', description: 'MoveNow works alongside your existing tools — no replacement, and no need to migrate what you already have.' },
            { title: 'Built by people who know the work', description: 'Built by practitioners with hands-on experience alongside engineers — designed around real scenarios, not just accuracy on paper.' },
            { title: 'Human-in-the-loop verification', description: 'Records that need a second look are flagged for human review — and are never silently recorded without it.' }
        ],
        infoTitle: 'Additional information',
        infoSubtitle: 'A polished product experience, support-ready contact options, and a system built to stay out of your way.',
        infoCards: [
            { title: 'Time savings', description: 'Automate repetitive work and free your team to focus on higher-value tasks.' },
            { title: 'Elegant experience', description: 'A calm, modern interface that feels premium from the first click.' },
            { title: 'Easy contact', description: 'Reach us when you need help, onboarding, or a custom rollout.' }
        ],
        contactTitle: 'Request a demo',
        contactSubtitle: 'Discover how MoveNow can take manual file work off your team’s plate.',
        contactDescription: 'Share a few details about your workflow and current setup. We will get back to you with a demo tailored to how your team works.',
        contactBenefits: [
            { title: 'Fast coordination', description: 'Send the form, and our team will reach out to find a time that works for you.' },
            { title: 'A guided tour, not a sales pitch', description: 'We will show you how MoveNow fits into your existing process.' },
            { title: 'Questions welcome', description: 'Finance team, IT, or just curious — tell us about your setup in the message field.' }
        ],
        demoCompanyLabel: 'Company',
        demoFullNameLabel: 'Full name',
        demoPhoneLabel: 'Phone',
        demoEmailLabel: 'Business email',
        demoMessageLabel: 'Message',
        demoMessagePlaceholder: 'Tell us about your needs, your current accounting system, or anything else worth knowing.',
        demoSubmitLabel: 'Request a demo',
        demoSuccessMessage: 'Your request was received correctly. We will hear from you soon.',
        demoFootnote: 'By submitting this form you agree to let the MoveNow team contact you about your request.',
        footerNavHeading: 'Explore',
        footerResourcesHeading: 'Resources',
        footerResourcesNote: 'Guides, documentation, and a help center are on the way.',
        footerResourceItems: ['Documentation', 'User guide', 'Help center'],
        footerComingSoonBadge: 'Soon',
        footerContactHeading: 'Get in touch',
        footerContactNote: 'Questions before you commit? We are happy to walk you through it.',
        footerContactCta: 'Talk to us',
        footerBackToTop: 'Back to top',
        footerRightsReserved: 'All rights reserved.'
    },
    he: {
        skipToContentLabel: 'דלג לתוכן',
        introBrand: 'InvoNow',
        introPresentsWord: 'מהפכה שקטה בהנהלת חשבונות',
        introScrollHint: 'גללו כדי להיכנס',
        appName: 'MoveNow',
        slogan: 'עיבוד קבצים אלגנטי לצוותים שמעריכים זמן.',
        navHome: 'בית',
        navWork: 'מוצר',
        navFit: 'התאמה לעסק',
        navBenefits: 'יתרונות עסקיים',
        navSecurity: 'אבטחה ואמינות',
        navInfo: 'בקשו הדגמה',
        login: 'התחברות',
        languageLabel: 'שפה',
        colorLabel: 'צבע ראשי',
        themeLabel: 'החלפת מצב תצוגה',
        heroBadge: 'מהיר. יעיל. אמין.',
        heroTitle: 'מחשבונית לפקודת הנהלת חשבונות בלי הקלדה ידנית.',
        heroSubtitle: ' MoveNow קוראת את חשבוניות הספקים שלכם, מחלצת ומאמתת את הנתונים באמצעות AI, ומעבירה פקודות יומן מוכנות להנהלת חשבונות ישירות למערכת הקיימת שלכם — בלי הטמעה מחדש, בלי שבועות של התקנות, בלי הסבת נתונים מורכבת ומסובכת ובלי השבתת עבודה לצורך הדרכות עובדים.',
        heroPrimary: 'בקשו הדגמה עכשיו',
        heroSecondary: 'לראות את הזרימה',
        heroStats: [
            { value: '24/7', description: 'זמינה תמיד לקליטת ועיבוד קבצים.' },
            { value: '3x', description: 'פחות חיכוך עם תהליכי עבודה יעילים.' },
            { value: '∞', description: 'מתרחב בצורה חלקה מצוותים קטנים ועד שימוש בארגון.' }
        ],
        heroPreview: {
            liveStatusLabel: 'סטטוס חי',
            filePipelineLabel: 'צינור עיבוד חשבוניות',
            processingBadge: 'בעיבוד',
            intakeLabel: 'קליטה',
            doneLabel: 'הושלם',
            processingStageLabel: 'עיבוד',
            fastLabel: 'מהיר',
            resultsLabel: 'תנועות יומן',
            readyLabel: 'מוכנות'
        },
        challengeTitle: 'האתגר',
        challengeSubtitle: 'צוואר הבקבוק',
        challengeCards: [
            { title: 'הקלדה ידנית של נתונים', description: 'כל שדה בחשבונית — ספק, תאריכים, סכומים, שורות פירוט — מוקלד ידנית למערכת הנהלת החשבונות.' },
            { title: 'תהליך גוזל זמן', description: 'חשבוניות מצטברות בתור, וממתינות שיהיה למישהו זמן לטפל בהן אחת אחת.' },
            { title: 'טעויות אנוש', description: 'סכומים, תאריכים ופרטי ספקים שהוקלדו בטעות מוצאים את דרכם לספרים.' },
            { title: 'פורמטים לא אחידים', description: 'כל ספק שולח חשבוניות בפורמט שונה, מה שמקשה לתקנן ולהאיץ את הטיפול הידני.' },
            { title: 'מערכות מנותקות', description: 'נתוני החשבונית לא זורמים לבד למערכת הנהלת החשבונות — מישהו צריך לגשר על הפער ידנית.' },
            { title: 'עבודת הנהלת חשבונות חוזרת', description: 'אנשי כספים מיומנים מבלים שעות בהקלדה חוזרת ובעלת ערך נמוך, במקום בעבודה בעלת ערך גבוה יותר.' }
        ],
        workTitle: 'מה אנחנו עושים',
        workSubtitle: 'בלי הטמעה מחדש, בלי שבועות של התקנות, בלי הסבת נתונים מורכבת ומסובכת ובלי השבתת עבודה לצורך הדרכות עובדים',
        workCards: [
            { title: 'קליטה', description: 'איסוף קבצים מהצוות או מהלקוחות בתהליך פשוט ומונחה.' },
            { title: 'עיבוד', description: 'המרת נתונים בצורה מהירה ועקבית עם פחות שלבים ידניים.' },
            { title: 'מסירה', description: 'שליחת תוצאות מסודרות שהצוות יכול להשתמש בהן מיד.' }
        ],
        pipelineMock: {
            heading: 'MoveNow — צינור עיבוד חשבוניות',
            liveLabel: 'תצוגה חיה',
            invoiceLabel: 'חשבונית',
            invoiceFileName: 'חשבונית_0138.pdf',
            scanLabel: 'סריקה',
            emailLabel: 'אימייל',
            whatsappLabel: 'WhatsApp',
            aiAnalysisLabel: 'ניתוח AI',
            extractingLabel: 'חילוץ ואימות',
            fieldsLabel: 'ספק · תאריכים · סכומים · מע"מ',
            accountingSystemLabel: 'מערכת הנהלת חשבונות',
            supplierLabel: 'ספק',
            supplierValue: 'InvoNow בע"מ',
            totalDueLabel: 'סכום לתשלום',
            receivedLabel: 'התקבל',
            breadcrumbExtraction: 'חשבונית וחילוץ נתונים',
            breadcrumbAnalysis: 'ניתוח',
            breadcrumbAccounting: 'מערכת הנהלת החשבונות שלכם'
        },
        howTitle: 'איך זה עובד',
        howBridgeLine: 'צינור שקט מהחשבונית ועד ליומן',
        howSubtitle: 'פועלת לצד האופן שבו הצוות שלכם כבר עובד. היא לא מחליפה את מערכת הנהלת החשבונות — היא מזינה אותה.',
        howSteps: [
            { title: 'קליטה והעלאה', description: 'חשבוניות מגיעות בסריקה או בהעלאה דיגיטלית, בכל פורמט שבו הספקים שולחים אותן — ללא צורך בהכנה מיוחדת.' },
            { title: 'ניתוח וחילוץ נתונים בעזרת AI', description: 'MoveNow קוראת כל חשבונית ומחלצת אוטומטית פרטי ספק, תאריכים, סכומים, מע"מ ושורות פירוט.' },
            { title: 'אימות ונתונים מובנים', description: 'הנתונים המחולצים נבדקים ומובנים לרשומות מוכנות להנהלת חשבונות. כל מה שדורש בדיקה אנושית מסומן לבדיקה מהירה — לא נטמע בערימה.' },
            { title: 'ייצוא ואינטגרציה', description: 'נתונים מאומתים זורמים ישירות למערכת הנהלת החשבונות שלכם, מוכנים לרישום — בלי הקלדה חוזרת.' }
        ],
        fitTitle: 'מתאים לעסקים קטנים וגדולים',
        fitSubtitle: 'בין אם מדובר בצוות קטן או בארגון גדול, החוויה נשארת נקייה וניתנת להרחבה.',
        fitDescription: 'משימוש יומיומי קל ועד עומסי עבודה כבדים, אותה זרימה אלגנטית שומרת על הצוות שלכם בתנועה.',
        fitCards: [
            { title: 'צוותים קטנים', description: 'עובדים מהר יותר עם תהליכים פשוטים, סטטוס ברור ופחות עומס תפעולי.' },
            { title: 'חברות בצמיחה', description: 'שומרים על זרימה חלקה גם כשהיקף העבודה גדל והאחריות מתרחבת.' },
            { title: 'ארגונים גדולים', description: 'שומרים על עקביות בין מחלקות עם תהליך שנבנה להיקפים גדולים.' }
        ],
        benefitsTitle: 'יתרונות עסקיים',
        benefitsSubtitle: 'עיבוד קבצים אוטומטי משנה את האופן שבו הצוות שלכם מחלק את זמנו — כבר היום.',
        benefitsCards: [
            { title: 'פחות טעויות', description: 'נתונים מובנים ומאומתים מפחיתים טעויות שמגיעות לרשומות שלכם.' },
            { title: 'עיבוד מהיר יותר', description: 'קבצים עוברים מהתיבה לתוצאה בשבריר מהזמן שדרשה הקלדה ידנית.' },
            { title: 'פחות עבודה ידנית', description: 'הצוות מפסיק להקליד נתונים ידנית ומקבל את הזמן הזה בחזרה לעבודה אחרת.' },
            { title: 'גדילה יחד אתכם', description: 'מתמודדת עם נפח קבצים גדל בלי גידול יחסי בכוח אדם.' },
            { title: 'אינטגרציה ללא מאמץ', description: 'פועלת לצד המערכות והתהליכים הקיימים שלכם — בלי פרויקט הטמעה כבד.' },
            { title: 'רף כניסה נמוך', description: 'פשוט מספיק כדי שכל איש צוות יוכל להפעיל אותה בביטחון מהיום הראשון.' }
        ],
        statsHeadline: 'נבנתה כדי להשתלב, לא לשבש',
        statsSubtitle: 'MoveNow נבנתה כדי שתשתלב בקלות בתהליך הקיים שלכם — לא עוד מערכת כבדה להטמעה.',
        stats: [
            { value: '0', label: 'תשתית', description: 'בלי שרתים או התקנות על העמדות שלכם.' },
            { value: '0', label: 'הקמת נתונים', description: 'בלי סביבה מורכבת או הגירת נתונים לפני שהצוות יכול להתחיל.' },
            { value: '0', label: 'כוח אדם נוסף', description: 'לא נדרשים מומחים ייעודיים — הצוות הקיים ממשיך לעבוד כרגיל.' }
        ],
        securityTitle: 'אבטחה ואמינות',
        securitySubtitle: 'MoveNow נבנתה סביב האופן שבו צוותים באמת עובדים — עם נקודות בקרה, לא אוטומציה עיוורת.',
        securityItems: [
            { title: 'פלט עקבי ומובנה', description: 'כל קובץ עובר אימות אוטומטי לפני שהוא מגיע למערכת שלכם, ושומר על עקביות הנתונים הקיימים שלכם.' },
            { title: 'בלי לשבש את המערכת שלכם', description: 'MoveNow פועלת לצד הכלים הקיימים שלכם — בלי החלפה, ובלי צורך להגר את מה שכבר יש לכם.' },
            { title: 'נבנתה על ידי אנשי מקצוע מהשטח', description: 'נבנתה על ידי אנשי מקצוע עם ניסיון מעשי לצד מהנדסים — סביב תרחישים אמיתיים, לא רק דיוק על הנייר.' },
            { title: 'בקרה אנושית במעגל', description: 'רשומות שדורשות בדיקה נוספת מסומנות לבדיקה אנושית — ולעולם לא נרשמות בשקט בלעדיה.' }
        ],
        infoTitle: 'מידע נוסף',
        infoSubtitle: 'חוויה יפה ומלוטשת, אפשרויות יצירת קשר ברורות ומערכת שלא מפריעה לעבודה.',
        infoCards: [
            { title: 'חיסכון בזמן', description: 'אוטומציה של עבודה חוזרת כדי לפנות את הצוות למשימות בעלות ערך גבוה.' },
            { title: 'חוויית משתמש אלגנטית', description: 'ממשק מודרני ורגוע שמרגיש איכותי מהקליק הראשון.' },
            { title: 'צור קשר בקלות', description: 'פנו אלינו לתמיכה, להטמעה או לפריסה מותאמת אישית.' }
        ],
        contactTitle: 'בואו נתחיל',
        contactSubtitle: 'בקשו הדגמה',
        contactDescription: 'גלו כיצד MoveNow יכולה להוריד מהצוות שלכם את עבודת הקבצים הידנית. ספרו לנו קצת על הארגון שלכם, ונחזור אליכם לתיאום סיור מודרך.',
        contactBenefits: [
            { title: 'תיאום מהיר', description: 'שלחו את הטופס, והצוות שלנו ייצור קשר לתיאום זמן שמתאים לכם.' },
            { title: 'סיור מודרך, לא מכירה', description: 'נראה לכם כיצד MoveNow משתלבת בתהליך הקיים שלכם.' },
            { title: 'שאלות מוזמנות', description: 'צוות כספים, IT, או סתם סקרנים — ספרו לנו על המערך שלכם בשדה ההודעה.' }
        ],
        demoCompanyLabel: 'חברה',
        demoFullNameLabel: 'שם מלא',
        demoPhoneLabel: 'טלפון',
        demoEmailLabel: 'אימייל עסקי',
        demoMessageLabel: 'הודעה',
        demoMessagePlaceholder: 'ספרו לנו על הצורך שלכם, מערכת ההנהלת החשבונות הנוכחית, או כל דבר נוסף שכדאי שנדע.',
        demoSubmitLabel: 'בקשו הדגמה',
        demoSuccessMessage: 'הבקשה שלכם התקבלה בהצלחה. נשמע מכם בקרוב.',
        demoFootnote: 'בשליחת הטופס אתם מאשרים שצוות MoveNow ייצור אתכם קשר בנוגע לבקשתכם.',
        footerNavHeading: 'ניווט מהיר',
        footerResourcesHeading: 'משאבים',
        footerResourcesNote: 'מדריכים, תיעוד ומרכז עזרה בדרך אלינו.',
        footerResourceItems: ['תיעוד', 'מדריך למשתמש', 'מרכז עזרה'],
        footerComingSoonBadge: 'בקרוב',
        footerContactHeading: 'צרו קשר',
        footerContactNote: 'יש שאלות לפני שמתחילים? נשמח להסביר ולהדריך.',
        footerContactCta: 'דברו איתנו',
        footerBackToTop: 'חזרה למעלה',
        footerRightsReserved: 'כל הזכויות שמורות.'
    }
};

// Deterministic-feeling but varied starfield: random per page load, stable for the component's lifetime.
function createSpaceParticles(count: number): SpaceParticle[] {
    return Array.from({ length: count }, () => ({
        top: Math.random() * 100,
        left: Math.random() * 100,
        size: 1 + Math.random() * 2.6,
        duration: 4 + Math.random() * 6,
        delay: -(Math.random() * 10),
        peakOpacity: 0.35 + Math.random() * 0.55,
        driftX: (Math.random() - 0.5) * 24
    }));
}

function createShootingStars(count: number): ShootingStar[] {
    return Array.from({ length: count }, (_, index) => ({
        top: 5 + Math.random() * 60,
        left: 5 + Math.random() * 55,
        duration: 12 + Math.random() * 9,
        delay: -(index * 6 + Math.random() * 5),
        angle: -18 - Math.random() * 10
    }));
}

@Component({
    selector: 'app-landing',
    standalone: true,
    imports: [LandingStarfield, LandingIntro, LandingHeader, LandingHero, LandingChallenge, LandingWork, LandingHow, LandingFit, LandingBenefits, LandingSecurity, LandingInfo, LandingContact, LandingFooter],
    templateUrl: './landing.component.html',
    styleUrl: './landing.component.scss'
})
export class Landing implements OnInit {
    private readonly platformId = inject(PLATFORM_ID);
    private readonly document = inject(DOCUMENT);
    private readonly layoutService = inject(LayoutService);
    private readonly formBuilder = inject(FormBuilder);
    private sectionObserver?: IntersectionObserver;

    constructor(private readonly landinglService: LandinglService) { }

    readonly language = signal<LandingLanguage>('he');
    readonly activeSection = signal<'home' | 'work' | 'fit' | 'benefits' | 'security' | 'contact'>('home');

    readonly text = computed(() => LANDING_TEXT[this.language()]);
    readonly isDarkTheme = this.layoutService.isDarkTheme;
    readonly spaceParticles: SpaceParticle[] = createSpaceParticles(70);
    readonly shootingStars: ShootingStar[] = createShootingStars(3);
    readonly contactBenefitIcons = ['pi-clock', 'pi-calendar', 'pi-comments'];

    readonly demoSubmitted = signal(false);
    readonly demoForm = this.formBuilder.nonNullable.group({
        company: ['', Validators.required],
        fullName: ['', Validators.required],
        phone: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        message: ['']
    });

    ngOnInit(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        const savedLanguage = localStorage.getItem('landing-language');
        if (savedLanguage === 'he' || savedLanguage === 'en') {
            this.language.set(savedLanguage);
        }

        this.applyLanguage(this.language());
    }

    ngAfterViewInit(): void {
        if (!isPlatformBrowser(this.platformId) || typeof IntersectionObserver === 'undefined') {
            return;
        }

        const sectionIds: Array<'home' | 'work' | 'fit' | 'benefits' | 'security' | 'contact'> = ['home', 'work', 'fit', 'benefits', 'security', 'contact'];

        this.sectionObserver = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        this.activeSection.set(entry.target.id as 'home' | 'work' | 'fit' | 'benefits' | 'security' | 'contact');
                    }
                }
            },
            {
                root: null,
                rootMargin: '-30% 0px -55% 0px',
                threshold: 0.1
            }
        );

        for (const sectionId of sectionIds) {
            const sectionElement = this.document.getElementById(sectionId);

            if (sectionElement) {
                this.sectionObserver.observe(sectionElement);
            }
        }
    }

    ngOnDestroy(): void {
        this.sectionObserver?.disconnect();
    }

    setLanguage(language: LandingLanguage): void {
        this.language.set(language);

        if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem('landing-language', language);
            this.applyLanguage(language);
        }
    }

    direction(): 'ltr' | 'rtl' {
        return this.language() === 'he' ? 'rtl' : 'ltr';
    }

    scrollToSection(sectionId: string): void {
        // Scroll directly instead of navigating with a fragment, so the URL never picks up a #.
        this.document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    }

    toggleDarkMode(): void {
        this.layoutService.layoutConfig.update((state) => ({ ...state, darkTheme: !state.darkTheme }));
    }

    submitDemoRequest(): void 
    {
        if (this.demoForm.invalid) {
            this.demoForm.markAllAsTouched();
            return;
        }

        this.demoSubmitted.set(false);

        const { company, fullName, email, phone, message } = this.demoForm.getRawValue();

        this.landinglService.submitRequesstForADemo(company, fullName, email, phone, message).subscribe({
            next: () => {
                this.demoSubmitted.set(true);
                this.demoForm.reset({ company: '', fullName: '', phone: '', email: '', message: '' });
            },
            error: () => {
                this.demoSubmitted.set(false);
            }
        });
    }

    private applyLanguage(language: LandingLanguage): void {
        this.document.documentElement.lang = language;
        this.document.documentElement.dir = language === 'he' ? 'rtl' : 'ltr';
    }
}
